import React, { useEffect, useState } from 'react';
import { saveProductsToDynamo, fetchProductsFromApi, deleteProductFromApi } from '../api/apiClient';

const Admin = () => {
  const [products, setProducts] = useState([]);
  const [groupedProducts, setGroupedProducts] = useState([]);
  const [newProduct, setNewProduct] = useState({});
  const [adding, setAdding] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingRow, setSavingRow] = useState(null); // Track which row is being saved
  const [deletingRow, setDeletingRow] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Function to group products by title
  const groupProductsByTitle = (productList) => {
    const sorted = productList.sort((a, b) => {
      const noA = parseInt(a.no) || 0;
      const noB = parseInt(b.no) || 0;
      return noA - noB;
    });

    const groups = {};
    sorted.forEach(prod => {
      const title = prod.title || 'Others';
      if (!groups[title]) {
        groups[title] = [];
      }
      groups[title].push(prod);
    });

    const grouped = Object.entries(groups).map(([title, prods]) => ({
      title,
      products: prods
    }));

    grouped.sort((a, b) => {
      const minNoA = parseInt(a.products[0]?.no) || 0;
      const minNoB = parseInt(b.products[0]?.no) || 0;
      return minNoA - minNoB;
    });

    return grouped;
  };

  useEffect(() => {
    fetchProductsFromApi()
      .then(data => {
        setProducts(data);
        setGroupedProducts(groupProductsByTitle(data));
      })
      .catch(() => setError('Failed to load products.'));
  }, []);


  const handleChange = (product, field, value) => {
    const updated = products.map(p => 
      (p.no === product.no) ? { ...p, [field]: value } : p
    );
    setProducts(updated);
    setGroupedProducts(groupProductsByTitle(updated));
  };

  const handleNewProductChange = (field, value) => {
    setNewProduct({ ...newProduct, [field]: value });
  };

  // Handle photo upload and convert to base64
  const handlePhotoChange = (product, file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const updated = products.map(p =>
        (p.no === product.no) ? { ...p, photo: e.target.result } : p
      );
      setProducts(updated);
      setGroupedProducts(groupProductsByTitle(updated));
    };
    if (file) {
      reader.readAsDataURL(file);
    }
  };

  const handleNewPhotoChange = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setNewProduct((prev) => ({ ...prev, photo: e.target.result }));
    };
    if (file) {
      reader.readAsDataURL(file);
    }
  };

  const notifyCartCountUpdate = () => {
    window.dispatchEvent(new Event('cartCountUpdate'));
  };

  const removeProductFromCartCache = (productNo) => {
    try {
      const cart = JSON.parse(localStorage.getItem('cartProducts') || '[]');
      const updatedCart = cart.filter((item) => Number(item.no) !== Number(productNo));
      localStorage.setItem('cartProducts', JSON.stringify(updatedCart));
      window.dispatchEvent(new Event('cartCountUpdate'));
    } catch {
      // Ignore cache cleanup errors.
    }
  };


  // Save all products (existing bulk save)
  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await saveProductsToDynamo(products);
      setSuccess('Products saved to DynamoDB!');
      localStorage.removeItem('cartProducts');
      notifyCartCountUpdate();
      // Refresh products
      const data = await fetchProductsFromApi();
      setProducts(data);
      setGroupedProducts(groupProductsByTitle(data));
    } catch {
      setError('Failed to save products to DynamoDB.');
    }
    setSaving(false);
  };

  // Save individual product
  const handleSaveRow = async (product) => {
    setSavingRow(product.no);
    setError('');
    setSuccess('');
    try {
      if (!product.no) {
        setError('Product number (no) is required.');
        setSavingRow(null);
        return;
      }
      let productToSave = { ...product };
      if (productToSave.photo && typeof productToSave.photo === 'object') {
        const file = productToSave.photo;
        productToSave.photo = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = e => resolve(e.target.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      }
      await saveProductsToDynamo([{ ...productToSave, no: Number(productToSave.no) }]);
      setSuccess('Product updated!');
      const data = await fetchProductsFromApi();
      setProducts(data);
      setGroupedProducts(groupProductsByTitle(data));
    } catch {
      setError('Failed to update product.');
    }
    setSavingRow(null);
  };

  const handleDeleteRow = async (product) => {
    const productNo = Number(product?.no);

    if (!productNo) {
      setError('Product number is required to delete.');
      return;
    }

    const confirmed = window.confirm(`Delete product ${productNo}? This will remove it from the database.`);
    if (!confirmed) {
      return;
    }

    setDeletingRow(productNo);
    setError('');
    setSuccess('');

    try {
      await deleteProductFromApi(productNo);
      const updated = products.filter((item) => Number(item.no) !== productNo);
      setProducts(updated);
      setGroupedProducts(groupProductsByTitle(updated));
      removeProductFromCartCache(productNo);
      setSuccess('Product deleted!');
    } catch {
      setError('Failed to delete product.');
    }

    setDeletingRow(null);
  };

  // Add new product
  const handleAddProduct = async (e) => {
    e.preventDefault();
    setAdding(true);
    setError('');
    setSuccess('');
    try {
      if (!newProduct.no) {
        setError('Product number (no) is required.');
        setAdding(false);
        return;
      }
      let productToAdd = { ...newProduct, no: Number(newProduct.no) };
      // If photo is a File object, convert to base64
      if (productToAdd.photo && typeof productToAdd.photo === 'object') {
        const file = productToAdd.photo;
        productToAdd.photo = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = e => resolve(e.target.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      }
      await saveProductsToDynamo([productToAdd]);
      setSuccess('Product added!');
      setNewProduct({});
      setShowAddModal(false);
      // Refresh products
      const data = await fetchProductsFromApi();
      setProducts(data);
      setGroupedProducts(groupProductsByTitle(data));
    } catch {
      setError('Failed to add product.');
    }
    setAdding(false);
  };

  return (
    <div className="container mt-4">
      <h2>Admin Product Editor</h2>
      {error && <div className="text-danger mb-2">{error}</div>}
      {success && <div className="text-success mb-2">{success}</div>}
      <div className="mb-3">
        <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
          Add New Product
        </button>
      </div>
      {/* Add Product Modal */}
      {showAddModal && (
        <div className="modal show" style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <form onSubmit={handleAddProduct}>
                <div className="modal-header">
                  <h5 className="modal-title">Add New Product</h5>
                  <button type="button" className="btn-close" onClick={() => setShowAddModal(false)}></button>
                </div>
                <div className="modal-body">
                  {products[0] && Object.keys(products[0])
                    .filter(col => col !== 'photo')
                    .map(col => (
                      <div className="mb-2" key={col}>
                        <label className="form-label">{col}</label>
                        <input
                          type="text"
                          className="form-control"
                          value={newProduct[col] || ''}
                          onChange={e => handleNewProductChange(col, e.target.value)}
                          required={col === 'no' || col === 'product_name'}
                        />
                      </div>
                    ))}
                  <div className="mb-2">
                    <label className="form-label">Photo</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={e => handleNewPhotoChange(e.target.files[0])}
                    />
                    {newProduct.photo && (
                      <img
                        src={newProduct.photo}
                        alt="New Product"
                        style={{ width: 40, height: 40, marginTop: 4 }}
                      />
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={adding}>
                    {adding ? 'Adding...' : 'Add'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      <div className="table-responsive">
        <table className="table table-bordered table-striped">
          <colgroup>
            {products[0] && Object.keys(products[0])
              .filter(col => col !== 'photo')
              .map(col => (
                <col key={col} style={{ minWidth: `${Math.max(100, col.length * 12)}px` }} />
              ))}
            <col style={{ minWidth: '120px' }} /> {/* Photo */}
            <col style={{ minWidth: '100px' }} /> {/* Action */}
          </colgroup>
          <thead>
            <tr>
              {products[0] && Object.keys(products[0])
                .filter(col => col !== 'photo')
                .map(col => (
                  <th key={col} style={{ minWidth: `${Math.max(100, col.length * 12)}px` }}>{col}</th>
                ))}
              <th style={{ minWidth: '120px' }}>Photo</th>
              <th style={{ minWidth: '100px' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {groupedProducts.map((group, gIdx) => (
              <React.Fragment key={gIdx}>
                <tr className="table-primary">
                  <td colSpan={products[0] ? Object.keys(products[0]).filter(col => col !== 'photo').length + 2 : 10} className="title-row" style={{ backgroundColor: '#e03535', color: '#fff', fontWeight: 'bold' }}>
                    {group.title}
                  </td>
                </tr>
                {group.products.map((prod, prodIdx) => (
                  <tr key={prod.no || prodIdx}>
                    {Object.keys(prod)
                      .filter(col => col !== 'photo')
                      .map(col => (
                        <td key={col} style={{ minWidth: `${Math.max(100, col.length * 12)}px` }}>
                          <input
                            type="text"
                            className="form-control"
                            value={prod[col]}
                            onChange={e => handleChange(prod, col, e.target.value)}
                            style={{ minWidth: `${Math.max(100, col.length * 12)}px` }}
                          />
                        </td>
                      ))}
                    <td style={{ minWidth: '120px' }}>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={e => handlePhotoChange(prod, e.target.files[0])}
                      />
                      {prod.photo && typeof prod.photo === 'string' && (
                        <img
                          src={prod.photo}
                          alt="Product"
                          style={{ width: 20, height: 40, marginTop: 4 }}
                        />
                      )}
                    </td>
                    <td style={{ minWidth: '100px' }}>
                      <button className="btn btn-success" onClick={() => handleSaveRow(prod)} disabled={savingRow === prod.no}>
                        {savingRow === prod.no ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        className="btn btn-danger ms-2"
                        onClick={() => handleDeleteRow(prod)}
                        disabled={deletingRow === prod.no}
                      >
                        {deletingRow === prod.no ? 'Deleting...' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <button className="btn btn-success" onClick={handleSave} disabled={saving}>
        {saving ? 'Saving...' : 'Save All'}
      </button>
    </div>
  );
};
export default Admin;